import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import * as Constants from 'constants/Constants';
import * as Types from './types';
import { toggleCreateConnection } from './agreementAction';
import { toggleSpinner } from './agreementAction';
import { RESET_SNACK_BAR, SHOW_DANGER_BAR, SHOW_SUCCESS_BAR } from '../../../../store/actions/types';
import { HIDE_TABLE } from './types';
import { getSelectedAccountUuid } from 'components/Common/Utils/ResourceUtils';
import { isNotBlank } from 'components/Common/Utils/StringUtils';
import authenticationManager from 'container/AuthenticationManager';
import automationAuthenticationManager from 'container/AutomationAuthenticationManager';
import {
    normalizeDeliverToOtherParties,
    normalizeMessageTypesForPut
} from '../utils/agreementV2Normalization';
import {
    buildRoutingKeysPayload,
    requiresTmsSelection,
    shouldPostRoutingKeys
} from '../utils/routingKeysUtils';

export {
    normalizeApiIdentifierEntry,
    normalizeDeliverToOtherParties,
    normalizeMessageTypesForPut
} from '../utils/agreementV2Normalization';

/** One row from `GET /data-sharing/v2/provider-config`, stored in `agreementV2.providerConfig`. */
function normalizeProviderConfigRow(provider) {
    return {
        providerAccountId: provider.providerAccountId,
        providerName: provider.providerName,
        providerCapabilities: Array.isArray(provider.providerCapabilities)
            ? provider.providerCapabilities
            : []
    };
}

// --- PUT `/data-sharing/v2/sharing-agreements/{id}` payload (flat body; response may be flat or wrapped) ---

const SHARING_AGREEMENT_DTO_KEYS = [
    'id',
    'customerAccount',
    'customerAccountName',
    'createdAt',
    'providerAccount',
    'providerAccountName',
    'direction',
    'validFrom',
    'validTo',
    'deliverToOtherParties',
    'messageTypes'
];

function withNormalizedDeliverToOtherParties(agreement) {
    if (agreement == null || typeof agreement !== 'object' || Array.isArray(agreement)) {
        return agreement;
    }
    return {
        ...agreement,
        deliverToOtherParties: normalizeDeliverToOtherParties(agreement.deliverToOtherParties)
    };
}

function buildSharingAgreementDtoForPut(agreement) {
    /** @type {Record<string, unknown>} */
    const dto = {};
    for (const key of SHARING_AGREEMENT_DTO_KEYS) {
        if (key === 'messageTypes') {
            dto.messageTypes = normalizeMessageTypesForPut(agreement.messageTypes);
            continue;
        }
        if (key === 'deliverToOtherParties') {
            dto.deliverToOtherParties = normalizeDeliverToOtherParties(
                agreement.deliverToOtherParties
            );
            continue;
        }
        if (agreement[key] !== undefined) {
            dto[key] = agreement[key];
        }
    }
    return dto;
}

/**
 * PUT body matches the legacy Trust Center client: flat agreement JSON at the root
 * (same shape as a successful browser PUT to `/data-sharing/v2/sharing-agreements/{id}`).
 */
function buildSharingAgreementPutRequestBody(agreement) {
    return buildSharingAgreementDtoForPut(agreement);
}

function getOidcUserFromSessionStorage() {
    const authManager = isNotBlank(sessionStorage.getItem('identity_provider'))
        ? automationAuthenticationManager
        : authenticationManager;
    const storageKey = `oidc.user:${authManager.settings.authority}:${authManager.settings.client_id}`;
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function getEmailFromIdToken(idToken) {
    if (!idToken || typeof idToken !== 'string') {
        return null;
    }

    const parts = idToken.split('.');
    if (parts.length < 2) {
        return null;
    }

    try {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.email || payload.Email || null;
    } catch {
        return null;
    }
}

export function getUpdatedByEmailFromState(getState) {
    const termsEmail = getState()?.termsAndConditions?.loggedInUserTerms?.email;
    if (isNotBlank(termsEmail)) {
        return String(termsEmail).trim();
    }

    const oidcUser = getOidcUserFromSessionStorage();
    const profileEmail = oidcUser?.profile?.email;
    if (isNotBlank(profileEmail)) {
        return String(profileEmail).trim();
    }

    const tokenEmail = getEmailFromIdToken(oidcUser?.id_token);
    if (isNotBlank(tokenEmail)) {
        return String(tokenEmail).trim();
    }

    return null;
}

function buildSharingAgreementUpdateHeaders(getState) {
    const headers = {
        Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
    };
    const updatedByEmail = getUpdatedByEmailFromState(getState);
    if (updatedByEmail) {
        headers.UpdatedByEmail = updatedByEmail;
    }

    return headers;
}

/** Flat or wrapped `{ sharingAgreementDto }` response from GET/PUT. */
function unwrapSharingAgreementResponse(data) {
    if (data == null) return null;
    if (typeof data !== 'object') return null;
    const obj = /** @type {Record<string, unknown>} */ (data);
    if (obj.sharingAgreementDto != null && typeof obj.sharingAgreementDto === 'object') {
        return /** @type {Record<string, unknown>} */ (obj.sharingAgreementDto);
    }
    return obj;
}

export const getV2SharingAgreementById = (id) => {
    return (dispatch) => {
        const agreementsV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/${id}`;
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        dispatch({
            type: Types.DISABLE_AGREEMENT_OPERATIONS
        });
        dispatch({
            type: Types.DISABLE_CONNECTION_OPERATIONS
        });
        dispatch(toggleSpinner(true));
        axios
            .get(agreementsV2Url, { headers })
            .then((response) => {
                const raw = response.data;
                const flat = unwrapSharingAgreementResponse(raw) ?? raw;
                if (flat !== undefined && flat !== null) {
                    dispatch(setSelectedV2SharingAgreement(flat));
                }
                dispatch(toggleCreateConnection(false));
            })
            .catch((error) => {
                if (error.response.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while creating a Sharing Agreements'
                    });
                }
            })
            .finally(() => {
                dispatch({
                    type: Types.ENABLE_AGREEMENT_OPERATIONS
                });
                // TODO: not sure if this is needed for Sharing Agreement V2
                dispatch({
                    type: Types.ENABLE_CONNECTION_OPERATIONS
                });
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.HIDE_SPINNER
                });
            });
    };
};

export const getV2SharingAgreementByProviderAccountId = (providerAccountId) => {
    return (dispatch) => {
        const agreementsV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements`;
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        const params = {
            query: `providerAccount==${providerAccountId}`
        };
        dispatch(toggleSpinner(true));
        dispatch({
            type: Types.DISABLE_AGREEMENT_OPERATIONS
        });
        dispatch({
            type: Types.RESET_V2_CONNECTION_AGREEMENTS
        });
        dispatch({
            type: Types.DISABLE_GETCONNECTION_OPERATIONS
        });
        axios
            .get(agreementsV2Url, { headers, params })
            .then((response) => {
                if (response.data !== undefined) {
                    dispatch({
                        type: Types.GET_V2_CONNECTION_AGREEMENTS,
                        payload: response?.data?.items
                    });
                }
                dispatch(toggleCreateConnection(false));
            })
            .catch((error) => {
                if (error.response.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while creating a Sharing Agreements'
                    });
                }
            })
            .finally(() => {
                dispatch({
                    type: Types.ENABLE_AGREEMENT_OPERATIONS
                });
                dispatch({
                    type: Types.ENABLE_GETCONNECTION_OPERATIONS
                });
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.HIDE_SPINNER
                });
            });
    };
};

export const updateV2SharingAgreement = (agreementV2, onSuccess, onError) => {
    return (dispatch, getState) => {
        const agreementsV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/${agreementV2.id}`;
        const headers = buildSharingAgreementUpdateHeaders(getState);
        dispatch({
            type: Types.DISABLE_AGREEMENT_OPERATIONS
        });
        dispatch({
            type: Types.DISABLE_CONNECTION_OPERATIONS
        });
        dispatch(toggleSpinner(true));
        axios
            .put(agreementsV2Url, buildSharingAgreementPutRequestBody(agreementV2), { headers })
            .then((response) => {
                const flat = unwrapSharingAgreementResponse(response?.data);
                if (flat != null) {
                    const merged = {
                        ...flat,
                        customerAccountName: get(
                            flat,
                            'customerAccountName',
                            agreementV2.customerAccountName
                        ),
                        createdAt: get(flat, 'createdAt', agreementV2.createdAt),
                        createdByUserId: get(flat, 'createdByUserId', agreementV2.createdByUserId),
                        updatedByUserId: get(flat, 'updatedByUserId', agreementV2.updatedByUserId),
                        updatedByEmail: get(flat, 'updatedByEmail', agreementV2.updatedByEmail),
                        updatedAt: get(flat, 'updatedAt', agreementV2.updatedAt)
                    };
                    dispatch({
                        type: Types.UPDATE_V2_SHARING_AGREEMENT,
                        payload: withNormalizedDeliverToOtherParties(merged)
                    });
                }
                onSuccess && onSuccess();
                dispatch({
                    type: SHOW_SUCCESS_BAR,
                    payload: 'Sharing Agreement updated successfully'
                });
            })
            .catch((error) => {
                if (error.response.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while updating a Sharing Agreements'
                    });
                }
                onError && onError();
            })
            .finally(() => {
                dispatch({
                    type: Types.ENABLE_AGREEMENT_OPERATIONS
                });
                dispatch({
                    type: Types.ENABLE_CONNECTION_OPERATIONS
                });
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.HIDE_SPINNER
                });
            });
    };
};

export const deleteV2SharingAgreement = (id, onSuccess, onError) => {
    return (dispatch) => {
        const agreementsV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/${id}`;
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        dispatch({
            type: Types.DISABLE_AGREEMENT_OPERATIONS
        });
        dispatch({
            type: Types.DISABLE_CONNECTION_OPERATIONS
        });
        dispatch(toggleSpinner(true));
        axios
            .delete(agreementsV2Url, { headers })
            .then(() => {
                dispatch({
                    type: SHOW_SUCCESS_BAR,
                    payload: 'Sharing Agreement deleted successfully'
                });
                onSuccess && onSuccess();
            })
            .catch((error) => {
                if (error.response?.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while deleting the sharing agreement'
                    });
                }
                onError && onError();
            })
            .finally(() => {
                dispatch({
                    type: Types.ENABLE_AGREEMENT_OPERATIONS
                });
                dispatch({
                    type: Types.ENABLE_CONNECTION_OPERATIONS
                });
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.HIDE_SPINNER
                });
            });
    };
};

function getRoutingKeysFailureMessage(error) {
    const description = error?.response?.data?.fault?.description;
    const base =
        'Sharing agreement was created, but routing keys could not be configured.';
    return description ? `${base} ${description}` : `${base} Please contact support or configure routing keys manually.`;
}

const ROUTING_KEYS_SUCCESS_SNACKBAR_DELAY_MS = 4000;
const SNACKBAR_SEQUENCE_GAP_MS = 150;

const ROUTING_KEYS_CREATED_MESSAGE = 'Routing keys were successfully created';
const ROUTING_KEYS_AVAILABLE_MESSAGE = 'Routing keys are available';

function dispatchSequentialSuccessSnackbars(dispatch, messages) {
    const queuedMessages = messages.filter(Boolean);

    queuedMessages.forEach((message, index) => {
        setTimeout(() => {
            if (index > 0) {
                dispatch({
                    type: RESET_SNACK_BAR,
                    payload: false
                });
                setTimeout(() => {
                    dispatch({
                        type: SHOW_SUCCESS_BAR,
                        payload: message
                    });
                }, SNACKBAR_SEQUENCE_GAP_MS);
                return;
            }

            dispatch({
                type: SHOW_SUCCESS_BAR,
                payload: message
            });
        }, index * ROUTING_KEYS_SUCCESS_SNACKBAR_DELAY_MS);
    });
}

export const createV2SharingAgreement = (agreementV2, onSuccess) => {
    return (dispatch, getState) => {
        const { tms, ...agreementWithoutTms } = agreementV2 ?? {};
        const customerAccountId = getSelectedAccountUuid(getState);
        const agreementsV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements`;
        const routingKeysUrl = `https://${Constants.TRUSTCENTER_ENDPOINT}/core/v2/routing-keys`;
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        const shouldCreateRoutingKeys = requiresTmsSelection(agreementWithoutTms.direction) && tms;
        let agreementCreated = false;
        let routingKeysFollowUpMessage = null;
        let routingKeysFailed = false;
        dispatch(toggleSpinner(true));
        dispatch({
            type: Types.DISABLE_AGREEMENT_OPERATIONS
        });
        dispatch({
            type: Types.DISABLE_CONNECTION_OPERATIONS
        });
        const messageTypesRaw = agreementWithoutTms?.messageTypes;
        const messageTypesIn =
            messageTypesRaw != null &&
                typeof messageTypesRaw === 'object' &&
                !Array.isArray(messageTypesRaw)
                ? messageTypesRaw
                : {};
        const putCoercion = buildSharingAgreementDtoForPut(agreementWithoutTms);
        axios
            .post(
                agreementsV2Url,
                {
                    ...agreementWithoutTms,
                    messageTypes: normalizeMessageTypesForPut(messageTypesIn),
                    customerAccount: customerAccountId,
                    deliverToOtherParties: putCoercion.deliverToOtherParties
                },
                { headers }
            )
            .then((response) => {
                agreementCreated = true;
                const created = unwrapSharingAgreementResponse(response?.data);
                const createdForState = withNormalizedDeliverToOtherParties(created) ?? created;
                const finalizeSuccess = () => {
                    if (createdForState?.id !== undefined) {
                        dispatch({
                            type: Types.ADD_V2_SHARING_AGREEMENT,
                            payload: createdForState
                        });
                    }
                    if (onSuccess) {
                        onSuccess(createdForState);
                    }
                };

                if (!shouldCreateRoutingKeys) {
                    finalizeSuccess();
                    return;
                }

                const routingKeysHeaders = {
                    ...headers,
                    accountId: customerAccountId
                };

                return axios
                    .get(routingKeysUrl, { headers: routingKeysHeaders })
                    .then((response) => {
                        const existingRoutingKeys = Array.isArray(response?.data)
                            ? response.data
                            : [];

                        if (!shouldPostRoutingKeys(existingRoutingKeys, tms)) {
                            routingKeysFollowUpMessage = ROUTING_KEYS_AVAILABLE_MESSAGE;
                            finalizeSuccess();
                            return;
                        }

                        return axios
                            .post(routingKeysUrl, buildRoutingKeysPayload(tms), {
                                headers: routingKeysHeaders
                            })
                            .then(() => {
                                routingKeysFollowUpMessage = ROUTING_KEYS_CREATED_MESSAGE;
                                finalizeSuccess();
                            });
                    })
                    .catch((error) => {
                        routingKeysFailed = true;
                        dispatch({
                            type: SHOW_DANGER_BAR,
                            payload: getRoutingKeysFailureMessage(error)
                        });
                        finalizeSuccess();
                    });
            })
            .catch((error) => {
                if (error.response.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while creating a Sharing Agreement'
                    });
                }
            })
            .finally(() => {
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.ENABLE_AGREEMENT_OPERATIONS
                });
                dispatch({
                    type: Types.ENABLE_CONNECTION_OPERATIONS
                });
                dispatch({
                    type: Types.HIDE_SPINNER
                });
                const successMessages = [];
                if (agreementCreated && !routingKeysFailed) {
                    successMessages.push('Sharing Agreement created successfully');
                }
                if (routingKeysFollowUpMessage) {
                    successMessages.push(routingKeysFollowUpMessage);
                }
                dispatchSequentialSuccessSnackbars(dispatch, successMessages);
            });
    };
};

export const getAllV2ProviderSummary = (accountId) => {
    const providerSummaryUrl = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/provider-summary`;
    const headers = {
        Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
    };
    const params = accountId ? { accountId } : {};
    return axios.get(providerSummaryUrl, { headers, params });
};

export const getAllV2ProviderConfig = () => {
    const providerConfigUrl = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/provider-config`;
    const headers = {
        Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
    };
    return axios.get(providerConfigUrl, { headers });
};

export const loadV2ProviderConfig = () => (dispatch) => {
    return getAllV2ProviderConfig()
        .then((response) => {
            const providerConfig = (response?.data ?? []).map(normalizeProviderConfigRow);
            dispatch({
                type: Types.GET_V2_PROVIDER_CONFIG,
                payload: providerConfig
            });
            return providerConfig;
        })
        .catch(() => []);
};

export const getCloudHubConnectorStatus = (accountId) => {
    return (dispatch) => {
        if (!accountId) {
            dispatch({
                type: Types.SET_CLOUD_HUB_CONNECTOR_STATUS,
                payload: false
            });
            return Promise.resolve(false);
        }
        dispatch({ type: Types.FETCH_CLOUD_HUB_CONNECTOR_STATUS });
        const cloudHubConnectorUrl = `https://${Constants.TRUSTCENTER_ENDPOINT}/integration/titan/v1/chc-status`;
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        return axios
            .get(cloudHubConnectorUrl, {
                headers,
                params: { accountId }
            })
            .then((response) => {
                const isActive =
                    response?.data?.isActive === true ||
                    response?.data?.active === true ||
                    response?.data?.chcActive === true;
                dispatch({
                    type: Types.SET_CLOUD_HUB_CONNECTOR_STATUS,
                    payload: isActive
                });
                return isActive;
            })
            .catch(() => {
                dispatch({
                    type: Types.SET_CLOUD_HUB_CONNECTOR_STATUS_ERROR
                });
                return false;
            });
    };
};

export const getProvidersV2 = () => {
    return (dispatch) => {
        const providerSummaryUrl = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/provider-config`;
        dispatch(toggleSpinner(true));
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };

        dispatch({
            type: HIDE_TABLE
        });
        dispatch({
            type: Types.DISABLE_CONNECTION_OPERATIONS
        });
        axios
            .get(providerSummaryUrl, {
                headers
            })
            .then((response) => {
                if (response?.data?.length > 0) {
                    dispatch({
                        type: Types.GET_V2_PROVIDER_CONFIG,
                        payload: response.data.map(normalizeProviderConfigRow)
                    });
                }
            })
            .catch((error) => {
                if (error?.response?.status !== 401) {
                    dispatch({
                        type: SHOW_DANGER_BAR,
                        payload: 'Error while retreiving provider config'
                    });
                }
            })
            .finally(() => {
                dispatch({
                    type: Types.ENABLE_CONNECTION_OPERATIONS
                });
                dispatch({
                    type: Types.SHOW_TABLE
                });
                dispatch(toggleSpinner(false));
                dispatch({
                    type: Types.HIDE_SPINNER
                });
            });
    };
};

export const getSharingAgreementV2 = ({
    page = Constants.PAGE,
    size = Constants.PAGE_SIZE,
    sortOrder = Constants.SORT_ORDER.ASC,
    sortField = Constants.SORT_FIELD_V2_PROVIDER,
    searchTerm = '',
    providerAccountId = ''
} = {}) => {
    return (dispatch, getState) => {
        const sharingAgreementV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements`;
        dispatch(toggleSpinner(true));
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        dispatch({ type: HIDE_TABLE });
        dispatch({ type: Types.DISABLE_CONNECTION_OPERATIONS });
        const providers = getState().agreementV2.providerConfig;
        const accountId = getSelectedAccountUuid(getState);
        return (
            //TODO: remove. Synchronous call is a workaround until we have provider summary as response in /sharing-agreements
            (isEmpty(providers) ? getAllV2ProviderConfig() : Promise.resolve({ data: providers }))
                .then((response) => {
                    const providerConfig = (response?.data ?? []).map(normalizeProviderConfigRow);
                    dispatch({
                        type: Types.GET_V2_PROVIDER_CONFIG,
                        payload: providerConfig
                    });
                    const queryParam = providerAccountId
                        ? `providerAccount==${providerAccountId}`
                        : searchTerm
                            ? `providerAccountName=re="${searchTerm}"`
                            : undefined;
                    return axios
                        .get(sharingAgreementV2Url, {
                            headers,
                            params: {
                                accountId,
                                pageSize: size,
                                pageIndex: page - 1,
                                ...(queryParam && { query: queryParam }),
                                sortBy: `${sortOrder === Constants.SORT_ORDER.ASC ? '' : '-'
                                    }${sortField}`
                            }
                        })
                        .then((response) => {
                            const data = response?.data;
                            const items = data?.items ?? [];
                            let payload =
                                searchTerm && !providerAccountId
                                    ? items.filter((item) =>
                                        (item?.providerAccountName ?? '')
                                            .toLowerCase()
                                            .includes(searchTerm.toLowerCase())
                                    )
                                    : items;
                            dispatch({
                                type: Types.GET_V2_SHARING_AGREEMENTS,
                                payload,
                                page,
                                size,
                                totalElement:
                                    searchTerm && !providerAccountId
                                        ? payload.length
                                        : data?.totalItems
                            });
                        });
                })
                .catch((error) => {
                    if (error?.response?.status !== 401) {
                        dispatch({
                            type: SHOW_DANGER_BAR,
                            payload: 'Error while retreiving sharing agreements'
                        });
                    }
                })
                .finally(() => {
                    dispatch({
                        type: Types.ENABLE_CONNECTION_OPERATIONS
                    });
                    dispatch({
                        type: Types.SHOW_TABLE
                    });
                    dispatch(toggleSpinner(false));
                    dispatch({
                        type: Types.HIDE_SPINNER
                    });
                })
        );
    };
};

export const getSharingAgreementV2ForVisibilityProvider = () => {
    return (dispatch, getState) => {
        const sharingAgreementV2Url = `https://${Constants.TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements`;
        dispatch(toggleSpinner(true));
        const headers = {
            Authorization: 'Bearer ' + sessionStorage.getItem('access_token')
        };
        dispatch({ type: HIDE_TABLE });
        dispatch({ type: Types.DISABLE_CONNECTION_OPERATIONS });
        const providers = getState().agreementV2.providerConfig;
        const accountId = getSelectedAccountUuid(getState);
        return (
            //TODO: remove. Synchronous call is a workaround until we have provider summary as response in /sharing-agreements
            (isEmpty(providers) ? getAllV2ProviderConfig() : Promise.resolve({ data: providers }))
                .then((response) => {
                    const providerConfig = (response?.data ?? []).map(normalizeProviderConfigRow);
                    dispatch({
                        type: Types.GET_V2_PROVIDER_CONFIG,
                        payload: providerConfig
                    });

                    // Helper function to fetch a single page
                    const fetchPage = (pageIndex) => {
                        return axios.get(sharingAgreementV2Url, {
                            headers,
                            params: {
                                accountId,
                                pageSize: 25, // API supports max 25 records at a time
                                pageIndex: pageIndex
                            }
                        });
                    };

                    // First call to get total items
                    return fetchPage(0)
                        .then((firstResponse) => {
                            const firstData = firstResponse?.data;
                            const totalItems = firstData?.totalItems ?? 0;
                            let allItems = firstData?.items ?? [];

                            // Calculate how many more pages we need to fetch
                            const remainingItems = totalItems - allItems.length;
                            if (remainingItems > 0) {
                                const additionalPages = Math.ceil(remainingItems / 25);
                                const pageIndexes = Array.from(
                                    { length: additionalPages },
                                    (_, i) => i + 1
                                ); // Start from pageIndex 1 (second page)

                                // Fetch all remaining pages in parallel
                                const additionalCalls = pageIndexes.map((pageIdx) =>
                                    fetchPage(pageIdx)
                                );

                                return Promise.all(additionalCalls).then((responses) => {
                                    responses.forEach((response) => {
                                        const pageItems = response?.data?.items ?? [];
                                        allItems = [...allItems, ...pageItems];
                                    });
                                    return { items: allItems, totalItems };
                                });
                            }
                            return { items: allItems, totalItems };
                        })
                        .then(({ items }) => {
                            dispatch({
                                type: Types.GET_V2_SHARING_AGREEMENTS,
                                payload: items
                            });
                        });
                })
                .catch((error) => {
                    if (error?.response?.status !== 401) {
                        dispatch({
                            type: SHOW_DANGER_BAR,
                            payload: 'Error while retrieving sharing agreements'
                        });
                    }
                })
                .finally(() => {
                    dispatch({
                        type: Types.ENABLE_CONNECTION_OPERATIONS
                    });
                    dispatch({
                        type: Types.SHOW_TABLE
                    });
                    dispatch(toggleSpinner(false));
                    dispatch({
                        type: Types.HIDE_SPINNER
                    });
                })
        );
    };
};

export const resetV2AgreementsList = () => ({
    type: Types.RESET_AGREEMENTS
});

export const setSelectedV2SharingAgreement = (agreement) => {
    return {
        type: Types.SET_SELECTED_V2_SHARING_AGREEMENT,
        payload: withNormalizedDeliverToOtherParties(agreement) ?? agreement
    };
};
